import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import StorageLocationAutocomplete from "./StorageLocationAutocomplete";
import SelectMobileLocationModal from "./SelectMobileLocationModal";

export default function SelectFixedLocation({ location, onClose, onSubmit }) {
  const [selectedLocation, setSelectedLocation] = useState({ warehouse: null, warehouse_locations: [] });
  const [overrideMobileLocation, setOverrideMobileLocation] = useState(false);
  const [mobileWarehouseLocation, setMobileWarehouseLocation] = useState(location);
  const [details, setDetails] = useState(null);
  const locationsVariables = {
    skipVirtual: true,
    matchLocationName: true,
    expandSlotSiblings: true,
    active: true,
  };

  const warehouseMapFilters = location?.virtualPalletLocation
    ? {
        virtualPalletLocation: location.virtualPalletLocation,
        warehouse: location.warehouse,
      }
    : null;

  function handleSubmit() {
    const locationData = details || {};
    const selectedWarehouseLocations = selectedLocation.warehouse_locations || [];

    // Normalize warehouse object to ensure snake_case keys
    const normalizeWarehouse = (warehouse) => {
      if (!warehouse) return null;
      return {
        id: warehouse.id,
        warehouse_id: warehouse.warehouseId || warehouse.warehouse_id,
        name: warehouse.name,
      };
    };

    const warehouseLocations = selectedWarehouseLocations.length
      ? selectedWarehouseLocations.map(location => ({
          id: location.id,
          locationId: location.locationId || location.location_id,
          locationName: location.locationName || location.location_name,
          zone: location.zone,
          warehouse: normalizeWarehouse(selectedLocation.warehouse || locationData.warehouse),
        }))
      : [
          {
            id: locationData.id,
            locationId: locationData.locationId || locationData.location_id,
            locationName: locationData.locationName || locationData.location_name,
            zone: locationData.zone,
            warehouse: normalizeWarehouse(selectedLocation.warehouse || locationData.warehouse),
          },
        ];

    onSubmit(
      warehouseLocations,
      {
        id: mobileWarehouseLocation.id,
        locationId: mobileWarehouseLocation.locationId,
        locationName: mobileWarehouseLocation.locationName || mobileWarehouseLocation.name
      },
      details
    );
  }

  function handleChange(value, details) {
    setSelectedLocation(value);
    setDetails(details);
  }

  function handleOverrideToggle() {
    setOverrideMobileLocation(!overrideMobileLocation);
  }

  const handleOverrideSubmit = data => {
    setMobileWarehouseLocation(data);
    handleOverrideToggle();
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography fontSize={24} fontWeight={400} align="center" color="text.primary">
          Select Base Location for
        </Typography>
        <Typography fontSize={20} fontWeight={500} align="center" color="text.primary">
          {mobileWarehouseLocation.locationName}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={1}>
          <StorageLocationAutocomplete
            autoOpen
            value={selectedLocation}
            warehouseMapFilters={warehouseMapFilters}
            warehouseId={location?.warehouse?.id}
            locationsVariables={locationsVariables}
            onChange={handleChange}
          />
        </Box>
        {overrideMobileLocation && (
          <SelectMobileLocationModal
            warehouseId={location?.warehouse?.id}
            onClose={handleOverrideToggle}
            onOverride={handleOverrideSubmit}
            onSubmit={onSubmit}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2, display: "flex", justifyContent: "space-between" }}>
        {location?.virtualPalletLocation ? (
          <Button variant="text" color="primary" onClick={handleOverrideToggle}>
            Change Suggested Location
          </Button>
        ) : (
          <div />
        )}
        <Box display="flex" gap={1}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!selectedLocation.warehouse_locations?.length}
          >
            Submit
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
